/** Creates and positions a transient attack hitbox rectangle in front of the owner's physics body. */
import Phaser from 'phaser';
import { normalize } from '../utils/Math';
import { ConfigLoader } from '../systems/ConfigLoader';

/** Manages a short-lived rectangular physics hitbox for melee attacks, tracking per-swing hit targets to prevent multi-hits. */
export class HitboxComponent {
  private scene: Phaser.Scene;
  private hitbox: Phaser.GameObjects.Rectangle | null = null;
  private ownerSprite: Phaser.GameObjects.Sprite;
  private hitEntities: Set<Phaser.GameObjects.GameObject> = new Set();
  private activeOffset = 0;

  constructor(scene: Phaser.Scene, ownerSprite: Phaser.GameObjects.Sprite) {
    this.scene = scene;
    this.ownerSprite = ownerSprite;
  }

  /** Owner body center in world coords. Hitbox emanates from here, not sprite center,
   *  because physics bodies sit at feet level — ~10px below sprite center. */
  private get ownerBodyCenter(): { x: number; y: number } {
    const body = this.ownerSprite.body as Phaser.Physics.Arcade.Body | null;
    if (body) {
      return { x: body.x + body.width / 2, y: body.y + body.height / 2 };
    }
    return { x: this.ownerSprite.x, y: this.ownerSprite.y };
  }

  get body(): Phaser.Physics.Arcade.Body | null {
    return this.hitbox?.body as Phaser.Physics.Arcade.Body | null;
  }

  get gameObject(): Phaser.GameObjects.Rectangle | null {
    return this.hitbox;
  }

  /** Return true if this target was already struck during the current active swing. */
  hasHit(target: Phaser.GameObjects.GameObject): boolean {
    return this.hitEntities.has(target);
  }

  /** Mark a target as hit so it cannot be damaged again in the same swing. */
  recordHit(target: Phaser.GameObjects.GameObject): void {
    this.hitEntities.add(target);
  }

  /** Spawn the hitbox rectangle in front of the owner based on facing direction, clearing any previous swing. */
  activate(facingX: number, facingY: number, attackType?: string): void {
    this.deactivate();
    this.hitEntities.clear();

    const skillHitbox = attackType
      ? ConfigLoader.getAttackConfig(attackType)?.hitbox
      : undefined;
    const { width, height, offset } = skillHitbox ?? ConfigLoader.combat.hitbox;
    this.activeOffset = offset;

    // Position hitbox in front of the owner's body center (not sprite center)
    const norm = normalize(facingX, facingY);
    const normX = norm.x === 0 && norm.y === 0 ? 0 : norm.x;
    const normY = norm.x === 0 && norm.y === 0 ? 1 : norm.y;

    const origin = this.ownerBodyCenter;
    const x = origin.x + normX * offset;
    const y = origin.y + normY * offset;

    this.hitbox = this.scene.add.rectangle(x, y, width, height, 0xff0000, 0);
    this.scene.physics.add.existing(this.hitbox, false);

    const body = this.hitbox.body as Phaser.Physics.Arcade.Body;
    body.setAllowGravity(false);
    body.setImmovable(true);
  }

  /** Destroy the active hitbox rectangle. */
  deactivate(): void {
    if (this.hitbox) {
      this.hitbox.destroy();
      this.hitbox = null;
    }
  }

  /** Reposition the active hitbox to track the owner's current location and facing direction. */
  updatePosition(facingX: number, facingY: number): void {
    if (!this.hitbox) return;

    const offset = this.activeOffset;
    const norm = normalize(facingX, facingY);
    const normX = norm.x === 0 && norm.y === 0 ? 0 : norm.x;
    const normY = norm.x === 0 && norm.y === 0 ? 1 : norm.y;

    const origin = this.ownerBodyCenter;
    this.hitbox.x = origin.x + normX * offset;
    this.hitbox.y = origin.y + normY * offset;
  }

  destroy(): void {
    this.deactivate();
  }
}
